import React, { useMemo, useState, useEffect } from "react";
import { menuItems } from "../../home/types/side-bar";
import { useSelector } from "react-redux";
import _ from "lodash";
import { useLogin } from "../../../common/hooks/useUser";
import "./navbar.scss";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { NavLink } from "react-router-dom";
import { MenuItem, Permission } from "../../home/types/menu-item";
import { SUPER_TENANT } from "../../../utils/constants";
import ArrowRight from "../../../assets/icons/arrow-right.svg";

const Navbar: React.FC = () => {
  const [navbarClickedItem, setNavbarClickedItem] = useState<number | null>(
    null
  );
  const [subMenuOpenItem, setSubMenuOpenItem] = useState<number | null>(null);
  const [nestedDropdownPosition, setNestedDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [mainDropdownPosition, setMainDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleMainMenuMouseEnter = (index: number, event: any) => {
    setNavbarClickedItem(index);
    const rect = event.currentTarget.getBoundingClientRect();
    setMainDropdownPosition({
      top: rect.bottom + 5, // Position below the nav item with 5px gap
      left: rect.left,
    });
  };

  const handleSubMenuMouseEnter = (
    subIndex: number,
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    setSubMenuOpenItem(subIndex);
    const rect = event.currentTarget.getBoundingClientRect();
    setNestedDropdownPosition({
      top: rect.top - 5,
      left: rect.right + 5, // Add 5px gap to the right
    });
  };

  const permissions = useSelector(
    (state: any) => state?.session?.userDetails?.roles?.rolePermissions
  );
  const tenantDetails = useLogin();

  const currentUserRoleId = useMemo(() => {
    return tenantDetails?.userDetails?.roles?.id;
  }, [tenantDetails]);

  const currentUserType = tenantDetails?.tenantDetails?.tenantType;
  const filterMenuByPermissions = (
    menuItems: MenuItem[] | undefined,
    permissions: Permission[]
  ): MenuItem[] => {
    if (!menuItems) return [];
    if (!permissions) return menuItems;

    return menuItems.filter((menu) => {
      const module: any = permissions?.find(
        (perm: any) => perm?.resource === menu?.moduleName
      );

      let hasMainMenuPermission: boolean = false;

      // If menu doesn't have a moduleName, allow access (like Dashboard)
      if (!menu.moduleName) {
        hasMainMenuPermission = true;
      }
      // If menu has moduleName, check permissions
      else if (module) {
        // If module exists in permissions, check read access (default to true if scopes not defined)
        hasMainMenuPermission = !module.scopes || module.scopes.read === true;
      } else {
        hasMainMenuPermission = false;
      }

      // const hasMainMenuPermission =
      //   !menu.moduleName || module?.permissions?.includes("read") || false;

      if (
        menu?.permissionRole?.length &&
        !menu.permissionRole.includes(currentUserRoleId)
      ) {
        return false;
      }
      if (menu?.onlyShowTOAFP) {
        return currentUserType === SUPER_TENANT && hasMainMenuPermission;
      }
      if (menu.subMenu) {
        menu.subMenu = filterMenuByPermissions(
          menu.subMenu,
          module?.subresources
        );

        return hasMainMenuPermission && menu.subMenu.length > 0;
      }

      return hasMainMenuPermission;
    });
  };
  // Close dropdowns when scrolling the main page (not dropdown itself)
  useEffect(() => {
    const handleScroll = (event: Event) => {
      const target = event.target as Element;
      
      // Don't close if scrolling within the dropdown
      if (target?.closest('.nav-submenu-dropdown') || 
          target?.closest('.nav-submenu-nested-dropdown')) {
        return;
      }
      setNavbarClickedItem(null);
      setSubMenuOpenItem(null);
    };

    // Listen for window scroll and navbar horizontal scroll
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const filterMenu = _.cloneDeep(menuItems);
  const menuFilterItems = filterMenuByPermissions(filterMenu, permissions);
  return (
    <nav className="navbar">
      <div className="navbar-main-items">
        {menuFilterItems
          .filter((menuItem) => menuItem.navbarItem !== false)
          .map((menuItem, index) => {
            const hasSubMenuItems =
              menuItem.subMenu && menuItem.subMenu.length > 0;
            return (
              <div
                key={index}
                className="nav-item"
                tabIndex={0}
                onMouseLeave={() => {
                  setNavbarClickedItem(null);
                  setSubMenuOpenItem(null);
                }}
              >
                <NavLink
                  to={menuItem.path}
                  className="nav-item-content"
                  onMouseEnter={(e) => handleMainMenuMouseEnter(index, e)}
                  onFocus={(e) => handleMainMenuMouseEnter(index, e)} // optional: open on keyboard focus
                >
                  <img
                    src={menuItem.icon}
                    alt={menuItem.label}
                    className="menu-icon"
                  />
                  <div>{menuItem.label}</div>
                  {hasSubMenuItems && navbarClickedItem === index && (
                    <KeyboardArrowUpIcon className="nav-main-arrow" />
                  )}
                  {hasSubMenuItems && navbarClickedItem !== index && (
                    <KeyboardArrowDownIcon className="nav-main-arrow" />
                  )}
                </NavLink>
                {hasSubMenuItems && navbarClickedItem === index && (
                  <div
                    className="nav-submenu-dropdown"
                    style={{
                      top: mainDropdownPosition?.top || 0,
                      left: mainDropdownPosition?.left || 0,
                    }}
                    // Optional: also keep open if mouse enters submenu again
                    onMouseEnter={() => setNavbarClickedItem(index)}
                  >
                    {menuItem.subMenu?.map((subMenuItem, subIndex) => (
                      <div key={subIndex} className="submenu-item-wrapper">
                        <NavLink
                          to={subMenuItem.path}
                          className="nav-submenu-item"
                          onMouseEnter={(e) =>
                            handleSubMenuMouseEnter(subIndex, e)
                          }
                          onFocus={() => setSubMenuOpenItem(subIndex)} // keyboard nav support
                          onBlur={() => setSubMenuOpenItem(null)} // keyboard nav support
                        >
                          <img
                            src={subMenuItem.icon}
                            alt={subMenuItem.label}
                            className="submenu-icon"
                          />
                          <div>{subMenuItem.label}</div>
                          {subMenuItem.subMenu &&
                            subMenuItem.subMenu.length > 0 &&
                            subMenuOpenItem !== subIndex && (
                              <ChevronRightIcon className="submenu-arrow" />
                            )}
                          {subMenuItem.subMenu &&
                            subMenuItem.subMenu.length > 0 &&
                            subMenuOpenItem === subIndex && (
                              <ChevronLeftIcon className="submenu-arrow" />
                            )}
                        </NavLink>
                        {subMenuItem.subMenu &&
                          subMenuItem.subMenu.length > 0 &&
                          subIndex === subMenuOpenItem && (
                            <div
                              className="nav-submenu-nested-dropdown"
                              style={{
                                top: nestedDropdownPosition?.top || 0,
                                left: nestedDropdownPosition?.left || 0,
                              }}
                              onMouseEnter={() => setSubMenuOpenItem(subIndex)}
                              onBlur={() => setSubMenuOpenItem(null)}
                            >
                              {subMenuItem.subMenu.map(
                                (nestedItem, nestedIndex) => (
                                  <NavLink
                                    key={nestedIndex}
                                    to={nestedItem.path}
                                    className="nav-submenu-item"
                                  >
                                    <img
                                      src={nestedItem.icon || ArrowRight}
                                      alt={nestedItem.label}
                                      className="submenu-icon"
                                    />
                                    <div>{nestedItem.label}</div>
                                  </NavLink>
                                )
                              )}
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </nav>
  );
};

export default Navbar;
